import type { AxiosPromise } from 'axios';
import axios from '~/services/axios';
import BaseClient, { type ExtendedAxiosRequestConfig } from './BaseClient';
import type {
    BandwidthCampaign,
    BandwidthTnOptionOrder,
    TcrBrandAppealCreate,
    TcrBrandCreate,
    TcrBrandUpdate,
    TcrCampaignCarrierTerms,
    TcrCampaignCreate,
    TcrCampaignCreated,
    TcrCampaignPacket,
    TcrCampaignPhoneNumbers,
    TcrCampaignPhoneNumbersAssign,
    TcrEnums,
    TcrLiveBrand,
    TcrLiveBrandDetails,
    TcrLiveBrandParams,
    TcrLiveCampaign,
    TcrLiveCampaignDetails,
    TcrLiveCampaignParams,
    TcrLiveCampaignSummary,
    TcrLivePhoneNumber,
    TcrLiveVetting,
    TcrPage,
    TcrSharing,
    TcrSupportingDocument,
    TcrUpload,
    TcrUploadUrl,
    TcrUsecaseQualification,
    TcrVettingClassCode,
    TcrVettingProvider,
} from './adminTenDlcRegistrationClient.types';

class AdminTenDlcRegistrationClient extends BaseClient {
    getEnums(config?: ExtendedAxiosRequestConfig): AxiosPromise<TcrEnums> {
        return this.makeCoreApiRequest('/api/admin/tcr/enums', 'get', null, null, config);
    }

    createUploadUrl(): AxiosPromise<TcrUploadUrl> {
        return this.makeCoreApiRequest('/api/admin/tcr/upload-urls', 'post');
    }

    private async upload(file: File): Promise<TcrUpload> {
        const { data } = await this.createUploadUrl();
        await axios.put(data.upload_url, file, { headers: { 'Content-Type': file.type } });

        return { upload_id: data.upload_id, file_name: file.name };
    }

    getBrands(params?: TcrLiveBrandParams, config?: ExtendedAxiosRequestConfig): AxiosPromise<TcrPage<TcrLiveBrand>> {
        return this.makeCoreApiRequest('/api/admin/tcr/brands', 'get', null, params, config);
    }

    createBrand(data: TcrBrandCreate): AxiosPromise<TcrLiveBrand> {
        return this.makeCoreApiRequest('/api/admin/tcr/brands', 'post', data);
    }

    getBrand(brandId: string, config?: ExtendedAxiosRequestConfig): AxiosPromise<TcrLiveBrandDetails> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/brands/${encodeURIComponent(brandId)}`,
            'get',
            null,
            null,
            config
        );
    }

    updateBrand(brandId: string, data: TcrBrandUpdate): AxiosPromise<TcrLiveBrand> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}`, 'put', data);
    }

    deleteBrand(brandId: string): AxiosPromise<void> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}`, 'delete');
    }

    revetBrand(brandId: string): AxiosPromise<TcrLiveBrand> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}/revet`, 'post');
    }

    appealBrand(brandId: string, data: TcrBrandAppealCreate): AxiosPromise<void> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}/appeal`, 'post', data);
    }

    async uploadBrandAppealEvidence(brandId: string, file: File): AxiosPromise<TcrSupportingDocument> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}/appeal/evidence`, 'post', {
            file: await this.upload(file),
        });
    }

    getBrandQualification(
        brandId: string,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<{ data: TcrUsecaseQualification[] }> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/brands/${encodeURIComponent(brandId)}/qualification`,
            'get',
            null,
            null,
            config
        );
    }

    requestBrandVetting(
        brandId: string,
        data: { vetting_class: TcrVettingClassCode; evp_id: TcrVettingProvider }
    ): AxiosPromise<TcrLiveVetting> {
        return this.makeCoreApiRequest(`/api/admin/tcr/brands/${encodeURIComponent(brandId)}/vetting`, 'post', data);
    }

    getCampaigns(
        params?: TcrLiveCampaignParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<TcrPage<TcrLiveCampaignSummary>> {
        return this.makeCoreApiRequest('/api/admin/tcr/campaigns', 'get', null, params, config);
    }

    async createCampaign(data: TcrCampaignCreate, screenshot: File | null): AxiosPromise<TcrCampaignCreated> {
        return this.makeCoreApiRequest('/api/admin/tcr/campaigns', 'post', {
            ...data,
            ...(screenshot ? { optin_screenshot: await this.upload(screenshot) } : {}),
        });
    }

    async uploadSupportingDocument(campaignId: string, file: File): AxiosPromise<TcrSupportingDocument> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/supporting-document`,
            'post',
            { file: await this.upload(file) }
        );
    }

    deleteSupportingDocument(campaignId: string, attachmentId: string): AxiosPromise<void> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/supporting-document/${encodeURIComponent(
                attachmentId
            )}`,
            'delete'
        );
    }

    getCampaign(campaignId: string, config?: ExtendedAxiosRequestConfig): AxiosPromise<TcrLiveCampaignDetails> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}`,
            'get',
            null,
            null,
            config
        );
    }

    updateCampaign(campaignId: string, data: TcrCampaignPacket): AxiosPromise<TcrLiveCampaign> {
        return this.makeCoreApiRequest(`/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}`, 'put', data);
    }

    deleteCampaign(campaignId: string): AxiosPromise<void> {
        return this.makeCoreApiRequest(`/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}`, 'delete');
    }

    shareCampaign(campaignId: string): AxiosPromise<TcrSharing> {
        return this.makeCoreApiRequest(`/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/share`, 'post');
    }

    getCampaignCarrierTerms(
        campaignId: string,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<TcrCampaignCarrierTerms> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/carrier-terms`,
            'get',
            null,
            null,
            config
        );
    }

    refreshCampaignCarrierTerms(
        campaignId: string,
        data: { mno_ids: string[] }
    ): AxiosPromise<Pick<TcrCampaignCreated, 'campaign_id' | 'mno_metadata'>> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/carrier-terms`,
            'post',
            data
        );
    }

    importCampaignIntoBandwidth(
        campaignId: string
    ): AxiosPromise<Pick<BandwidthCampaign, 'campaign_id' | 'bandwidth_id'>> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/bandwidth-import`,
            'post'
        );
    }

    getCampaignPhoneNumbers(
        campaignId: string,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<TcrCampaignPhoneNumbers> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/phone-numbers`,
            'get',
            null,
            null,
            config
        );
    }

    assignCampaignPhoneNumbers(
        campaignId: string,
        data: TcrCampaignPhoneNumbersAssign
    ): AxiosPromise<BandwidthTnOptionOrder> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/phone-numbers`,
            'post',
            data
        );
    }

    detachCampaignPhoneNumber(campaignId: string, phoneNumber: string): AxiosPromise<BandwidthTnOptionOrder> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/campaigns/${encodeURIComponent(campaignId)}/phone-numbers/${encodeURIComponent(
                phoneNumber
            )}`,
            'delete'
        );
    }

    getPhoneNumber(phoneNumber: string, config?: ExtendedAxiosRequestConfig): AxiosPromise<TcrLivePhoneNumber> {
        return this.makeCoreApiRequest(
            `/api/admin/tcr/phone-numbers/${encodeURIComponent(phoneNumber)}`,
            'get',
            null,
            null,
            config
        );
    }
}

export default new AdminTenDlcRegistrationClient();
